import styled from '@emotion/styled';
import {Select, SelectChangeEvent} from '@mui/material';
import React, {useState} from 'react';

import {ChevronSmallDown} from '@ELib/assets/images';
import {
  mixinInputDisabled,
  mixinInputError,
  mixinInputValid,
  mixinSVGIcon,
} from '@ELib/styles/mixins';

import {EMenuItem} from '../EMenu';

import {ECheckbox} from './ECheckbox';
import {FormInputWrapper} from './FormInputWrapper';
import {SharedProps} from './sharedProps';

export type ESelectOption = {
  value: string;
  displayText: string | React.ReactElement;
  /** Optional property if the text that should be displayed in the dropdown differs from the displayText  */
  dropdownDisplayText?: string | React.ReactElement;
};

export type ESelectProps = SharedProps & {
  placeholder?: string;
  value?: string;
  onChange?: (e: SelectChangeEvent<string>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  options: ESelectOption[];
  /* is the user allowed to select multiple options */
  multiple?: boolean;
  align?: 'left' | 'right';
};

const isEventTargetValueString = (
  e: SelectChangeEvent<unknown>
): e is SelectChangeEvent<string> => typeof e.target.value === 'string';

export const ESelect = ({
  label,
  labelInfo,
  bottomText,
  placeholder,
  value,
  onChange,
  options,
  onBlur,
  error,
  errorMessage,
  multiple = false,
  name,
  autoFocus,
  ariaLabel,
  disabled = false,
  align = 'left',
}: ESelectProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const selectedOptions = valuesToOptions(value, options);
  const validValues = optionsToValues(selectedOptions);
  const renderedValue = optionsToFormattedValues(selectedOptions, multiple);

  const handleChange = (e: SelectChangeEvent<unknown>) => {
    if (!onChange || !isEventTargetValueString(e)) {
      return;
    }
    onChange(e);
  };

  if (
    multiple &&
    options.find(
      (option) =>
        typeof option.displayText !== 'string' ||
        (option.dropdownDisplayText &&
          typeof option.dropdownDisplayText !== 'string')
    )
  ) {
    throw Error(
      'if multiple is true only string type displayText (or dropdownDisplayText) is supported'
    );
  }

  return (
    <FormInputWrapper
      bottomText={bottomText}
      error={error}
      errorMessage={errorMessage}
      label={label}
      labelInfo={labelInfo}
      labelProps={{htmlFor: name}}
    >
      <StyledSelect
        autoFocus={autoFocus}
        className={`ESelect${open ? ' ESelect-open' : ''}`}
        data-testid={`ESelect-${name}`}
        disabled={disabled}
        IconComponent={ChevronSmallDown}
        inputProps={{
          'aria-label': ariaLabel,
        }}
        multiple={multiple}
        name={name}
        open={open}
        renderValue={() => renderedValue}
        value={validValues}
        displayEmpty
        onBlur={onBlur}
        onChange={handleChange}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
      >
        {placeholder ? (
          <EMenuItem value={undefined} disabled>
            {placeholder}
          </EMenuItem>
        ) : null}
        {options.map(
          ({value: optionValue, displayText, dropdownDisplayText}) => (
            <EMenuItem
              key={optionValue}
              align={align}
              data-testid={`ESelect-${name}-option-${optionValue}`}
              value={optionValue}
            >
              {multiple ? (
                <ECheckbox
                  label={
                    (dropdownDisplayText as string) || (displayText as string)
                  }
                  value={value !== undefined && value.indexOf(optionValue) > -1}
                />
              ) : (
                dropdownDisplayText || displayText
              )}
            </EMenuItem>
          )
        )}
      </StyledSelect>
    </FormInputWrapper>
  );
};

const StyledSelect = styled(Select)<{error?: boolean}>`
  & .MuiInputBase-input {
    ${({error}) => (error ? mixinInputError : mixinInputValid)};
  }
  svg {
    ${mixinSVGIcon}
  }

  &.Mui-disabled {
    ${mixinInputDisabled}
  }

  .MuiSelect-icon {
    right: var(--gutter-small);
  }
`;

const valuesToOptions = (
  value: string | undefined,
  options: ESelectOption[]
): ESelectOption[] =>
  value
    ? (value
        .split(',')
        .map((valueSplit) =>
          options.find((option) => option.value === valueSplit)
        )
        .filter((option) => option !== undefined) as ESelectOption[])
    : [];

const optionsToValues = (options: ESelectOption[]) =>
  options.map((option) => option.value).join(',');

const optionsToFormattedValues = (
  options: ESelectOption[],
  multiple: boolean
) => {
  if (multiple) {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return options
      .map((option) => option.displayText || option.value)
      .join(', ');
  } else {
    return options[0]?.displayText || options[0]?.value;
  }
};
